import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ToastContainer } from 'react-toastify';
import { FaCalendarAlt, FaSearch, FaFilter } from 'react-icons/fa';
import { useTitle } from '../../../shared/context/TitleContext';
import { apiGet } from '../../../shared/utils/apiConfig';
import { showErrorToast } from '../../../shared/utils/toastHelper';

const dayNamesAr = {
  saturday: 'السبت',
  sunday: 'الأحد',
  monday: 'الإثنين',
  tuesday: 'الثلاثاء',
  wednesday: 'الأربعاء',
  thursday: 'الخميس',
  friday: 'الجمعة'
};

function formatDay(key) {
  if (!key) return '-';
  const lower = String(key).toLowerCase();
  return dayNamesAr[lower] || key;
}

function formatDate(dateString) {
  if (!dateString) return '-';
  try {
    const d = new Date(dateString);
    return d.toLocaleDateString('ar-EG', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return dateString;
  }
}

const DoctorMyReservations = () => {
  const { setTitle } = useTitle();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    setTitle('الحجوزات');
  }, [setTitle]);

  useEffect(() => {
    let isMounted = true;
    const fetchReservations = async () => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams();
        if (fromDate) queryParams.append('from_date', fromDate);
        if (toDate) queryParams.append('to_date', toDate);
        const url = queryParams.toString() 
          ? `/doctor_dash/doctor_reservations?${queryParams.toString()}`
          : '/doctor_dash/doctor_reservations';
        const data = await apiGet(url);
        if (!isMounted) return;
        const list = data?.result || data?.data || [];
        setReservations(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!isMounted) return;
        showErrorToast('فشل تحميل الحجوزات');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchReservations();
    return () => {
      isMounted = false;
    };
  }, [fromDate, toDate]);

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
  };

  const rows = useMemo(() => reservations.map((r) => ({
    id: r.id,
    patient_name: r.patient_name || r.client_name || '-',
    patient_phone: r.patient_phone || r.client_phone || '-',
    clinic: r.clinic || r.clinic_name || '-',
    day: formatDay(r.day),
    slot: r.slot || r.time || '-',
    appointment_type: r.appointment_type || '-',
    appointment_price: r.appointment_price != null ? r.appointment_price : '-',
    pay_type: r.pay_type || '-',
    payment_status: r.payment_status || '-',
    status: r.status || '-',
    date: formatDate(r.date)
  })), [reservations]);

  return (
    <div className="container-fluid">
      <ToastContainer />

      <div className="d-flex justify-content-between align-items-center my-4">
        <h5 className="mb-0">الحجوزات</h5>
      </div>

      {/* Filters */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">من تاريخ</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">إلى تاريخ</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <button
                className="btn btn-secondary d-flex align-items-center gap-2 w-100"
                onClick={handleClearFilters}
              >
                <FaFilter />
                مسح الفلاتر
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5">
              <FaCalendarAlt className="mb-2" />
              <p className="text-muted mb-0">لا توجد حجوزات</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-modern">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>اسم المريض</th>
                    <th>هاتف المريض</th>
                    <th>العيادة</th>
                    <th>اليوم</th>
                    <th>الميعاد</th>
                    <th>النوع</th>
                    <th>السعر</th>
                    <th>نوع الدفع</th>
                    <th>الحالة المالية</th>
                    <th>الحالة</th>
                    <th>التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.id ?? idx}>
                      <td>{idx + 1}</td>
                      <td>{r.patient_name}</td>
                      <td>{r.patient_phone}</td>
                      <td>{r.clinic}</td>
                      <td>{r.day}</td>
                      <td>{r.slot}</td>
                      <td>{r.appointment_type}</td>
                      <td>{r.appointment_price}</td>
                      <td>{r.pay_type}</td>
                      <td>{r.payment_status}</td>
                      <td>{r.status}</td>
                      <td>{r.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DoctorMyReservations;


